import { fmtNum } from './format'
import { PLAN_DEFAULT_DEPTH } from './lib'

// ---------------------------------------------------------------------------
// Pisos (IfcBuildingStorey) para as plantas.
//
// As vistas 2D precisam da cota no referencial do MUNDO three, que é
// onde vivem os triângulos que o `collectWorldTriangles` devolve. O
// IFC dá-a no referencial do FICHEIRO (Elevation autoral, nas unidades
// do projecto). Este módulo é puro: quem chama dá-lhe a ponte entre os
// dois (`DeclaredStoreyContext`, montado em `storeyContext.ts` a partir
// do leitor do IFC e da matriz do modelo).
//
// DE ONDE VEM A COTA (por ordem):
//
//   1. DECLARADA: `IfcBuildingStorey.Elevation`, que a norma define em
//      relação ao 0,00 do edifício, LIDO DO FICHEIRO pelo leitor do IFC e
//      convertido a metros com a unidade de comprimento do projecto
//      (o valor que o motor Fragments devolve já vem convertido pelo
//      importador; voltar a multiplicá-lo pela unidade dava 3,14 mm em
//      vez de 3,14 m num projecto em milímetros), somado à cota absoluta
//      do placement pai
//      (o do edifício) e levada ao referencial do MUNDO pela mesma
//      transformação que o motor aplica ao modelo (recentramento do
//      conversor + georreferenciação). É o "desvio" mundo-ficheiro, e é
//      determinístico. Só se usa quando o leitor do IFC está disponível
//      (unidade e placement lidos do ficheiro): sem unidade declarada não
//      se converte nada (não se assume metros).
//   2. CALCULADA: a caixa do próprio piso, ou a base dos elementos
//      contidos. É só recurso: a base de uma laje ou de uma sapata NÃO é
//      a cota do piso (um Piso 0 a 0,00 com laje de 0,30 lia-se -0,30), e
//      a UI marca-a "(calculado)".
//
// O `Elevation` autoral vai sempre no resultado, em metros, para a UI o
// mostrar ao lado.
// ---------------------------------------------------------------------------

export interface Storey2D {
  localId: number
  name: string
  /** Cota do piso no referencial do MUNDO three (metros). */
  elevation: number
  /** `IfcBuildingStorey.Elevation` autoral, em METROS, quando legível:
   *  o lido do ficheiro e convertido pela unidade do projecto, ou, sem
   *  leitor do IFC, o que o importador Fragments já devolve convertido.
   *  Só para mostrar; a geração usa `elevation`. */
  ifcElevation: number | null
  /** 'declarada': `elevation` vem do Elevation do IFC (ver topo).
   *  'calculada': vem da geometria e a UI marca-a "(calculado)". */
  elevationSource: 'declarada' | 'calculada'
}

/** O que é preciso para levar o `Elevation` declarado ao mundo. Tudo
 *  lido do ficheiro; sem isto a cota é calculada pela geometria. */
export interface DeclaredStoreyContext {
  /** `Elevation` do piso lido do ficheiro e já convertido a METROS pela
   *  unidade de comprimento do projecto. Null quando não se lê. */
  elevationM: (localId: number) => number | null
  /** Cota absoluta, em metros no referencial do ficheiro, do placement
   *  a que o piso se refere (o do edifício). Null quando não se lê. */
  parentZ: (localId: number) => number | null
  /** Cota do ficheiro (metros) para a coordenada y do mundo three. */
  fileHeightToWorldY: (zMetres: number) => number
}

/** y do mundo de um ponto à cota `zMetres` do ficheiro, para um modelo
 *  com a matriz de mundo `m` (16 valores, por colunas, como o three) e o
 *  recentramento `t` do conversor (ver `modelRecentre`). A geometria do
 *  motor está em (ficheiro em eixos three) + t; a matriz leva-a ao
 *  mundo. Puro, para poder ser testado sem three. */
export function fileHeightToWorldY(
  m: ArrayLike<number>,
  t: readonly [number, number, number],
  zMetres: number,
): number {
  const x = t[0]
  const y = zMetres + t[1]
  const z = t[2]
  return m[1] * x + m[5] * y + m[9] * z + m[13]
}

interface BoxLike {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
}

export interface StoreySource {
  getItemsOfCategories: (
    categories: RegExp[],
  ) => Promise<Record<string, number[]>>
  getItemsData: (ids: number[], config?: unknown) => Promise<unknown[]>
  getBoxes: (ids: number[]) => Promise<BoxLike[]>
}

function readAttr(data: unknown, key: string): unknown {
  if (!data || typeof data !== 'object') return null
  const v = (data as Record<string, unknown>)[key]
  if (v == null) return null
  if (typeof v === 'object' && 'value' in (v as object)) {
    return (v as { value: unknown }).value
  }
  return v
}

function readNumber(data: unknown, key: string): number | null {
  const v = readAttr(data, key)
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

/** Extrai os pisos de um modelo, com a cota no referencial do mundo.
 *  Nunca lança: um modelo sem pisos legíveis devolve []. */
export async function collectStoreys(
  model: StoreySource,
  descendantsOf?: (localId: number) => number[],
  declared?: DeclaredStoreyContext | null,
): Promise<Storey2D[]> {
  let ids: number[]
  try {
    const byCat = await model.getItemsOfCategories([/^IFCBUILDINGSTOREY$/i])
    ids = byCat['IFCBUILDINGSTOREY'] ?? Object.values(byCat)[0] ?? []
  } catch {
    return []
  }
  if (ids.length === 0) return []

  let data: unknown[]
  try {
    data = await model.getItemsData(ids, {
      attributesDefault: true,
      relationsDefault: { attributes: false, relations: false },
    })
  } catch {
    data = []
  }

  let boxes: BoxLike[]
  try {
    boxes = await model.getBoxes(ids)
  } catch {
    boxes = []
  }

  const out: Storey2D[] = []
  for (let i = 0; i < ids.length; i++) {
    const localId = ids[i]
    const attrs = data[i] ?? null
    const rawName = readAttr(attrs, 'Name')
    const name =
      typeof rawName === 'string' && rawName.trim()
        ? rawName.trim()
        : `Piso #${localId}`
    const declaredM = declared ? declared.elevationM(localId) : null
    const ifcElevation =
      declaredM !== null && Number.isFinite(declaredM)
        ? declaredM
        : readNumber(attrs, 'Elevation')

    let elevation: number | null = null
    let elevationSource: Storey2D['elevationSource'] = 'calculada'
    if (declared && declaredM !== null && Number.isFinite(declaredM)) {
      const parentZ = declared.parentZ(localId)
      if (parentZ !== null && Number.isFinite(parentZ)) {
        const y = declared.fileHeightToWorldY(parentZ + declaredM)
        if (Number.isFinite(y)) {
          elevation = y
          elevationSource = 'declarada'
        }
      }
    }
    const b = boxes[i]
    if (elevation === null && b && Number.isFinite(b.min?.y)) elevation = b.min.y
    if (elevation === null && descendantsOf) {
      // Sem caixa própria: cai-se para a base dos elementos contidos.
      const kids = descendantsOf(localId).slice(0, 200)
      if (kids.length > 0) {
        try {
          const kidBoxes = await model.getBoxes(kids)
          let minY = Infinity
          for (const kb of kidBoxes) {
            if (kb && Number.isFinite(kb.min?.y)) minY = Math.min(minY, kb.min.y)
          }
          if (Number.isFinite(minY)) elevation = minY
        } catch {
          /* fica sem cota */
        }
      }
    }
    if (elevation === null || !Number.isFinite(elevation)) continue
    out.push({ localId, name, elevation, ifcElevation, elevationSource })
  }
  out.sort((a, b) => a.elevation - b.elevation)
  return out
}

/** Profundidade de uma planta: do plano de corte (cota + offset) até ao
 *  piso IMEDIATAMENTE ABAIXO. Sem piso abaixo, usa-se a profundidade por
 *  omissão da fase 1 (4 m).
 *
 *  `elevations` é a lista de cotas de TODOS os pisos (em qualquer
 *  ordem). A comparação usa uma folga de 1 mm para dois pisos à mesma
 *  cota (acontece em modelos federados) não gerarem profundidade nula. */
export function planDepthFor(
  elevation: number,
  cutOffset: number,
  elevations: number[],
  fallback = PLAN_DEFAULT_DEPTH,
): number {
  const EPS = 1e-3
  let below = -Infinity
  for (const e of elevations) {
    if (e < elevation - EPS && e > below) below = e
  }
  if (!Number.isFinite(below)) return fallback
  const depth = elevation + cutOffset - below
  return depth > EPS ? depth : fallback
}

/** Cota do piso como se mostra na lista: a calculada pela geometria vai
 *  marcada, para não se confundir com a declarada no IFC. */
export function storeyCotaLabel(s: Storey2D): string {
  return `${fmtNum(s.elevation)} m${s.elevationSource === 'calculada' ? ' (calculado)' : ''}`
}

/** Tooltip do botão do piso: a cota usada, de onde vem e o Elevation
 *  autoral (em metros). */
export function storeyCotaTitle(s: Storey2D, cutOffset: number): string {
  const origem =
    s.elevationSource === 'declarada'
      ? 'Elevation declarado no IFC, levado ao referencial do mundo'
      : 'calculada pela geometria do piso, porque o Elevation do IFC não se pôde ler com unidade e placement'
  const autoral =
    s.ifcElevation !== null
      ? `. Elevation do IFC: ${fmtNum(s.ifcElevation)} m`
      : ''
  return (
    `Planta com o corte a ${fmtNum(cutOffset)} m acima da cota ` +
    `${fmtNum(s.elevation)} m (${origem})${autoral}.`
  )
}
